/**
 * Infinite-scroll sentinel hook (PHASE1 §7). Returns a ref to attach to a
 * sentinel element near the end of a list; when it scrolls into view and there
 * is more data (and no load is in flight), `onLoadMore` fires.
 */
import { useEffect, useRef } from 'react';

export interface InfiniteScrollOptions {
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  /** Root-margin so loading starts before the sentinel is fully visible. */
  rootMargin?: string;
}

export function useInfiniteScroll<T extends Element = HTMLDivElement>({
  hasMore,
  isLoading,
  onLoadMore,
  rootMargin = '200px',
}: InfiniteScrollOptions) {
  const sentinelRef = useRef<T | null>(null);
  const onLoadMoreRef = useRef(onLoadMore);

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  });

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || isLoading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadMoreRef.current();
        }
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isLoading, rootMargin]);

  return sentinelRef;
}
